import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FiMessageSquare, FiX, FiSend, FiSearch, FiCheck,
    FiArrowLeft, FiVolume2, FiVolumeX
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../config/supabase';
import {
    getConversations, getOrCreateConversation, getMessages, sendMessage,
    markMessagesAsRead, deleteConversation, getAvailableUsers,
    subscribeToMessages, subscribeToReadReceipts, getUnreadMessageCount,
    subscribeToConversationMessages
} from '../../lib/supabase/messages';
import messageSound from '../../lib/soundUtils/messageSound';

const MessageMenu = ({ isOpen, onClose, onRefreshBadge, isSidebarOpen }) => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();

    // State management
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messageText, setMessageText] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(0.7);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);

    // New conversation
    const [showNewConversation, setShowNewConversation] = useState(false);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [searchUsers, setSearchUsers] = useState('');

    const menuRef = useRef(null);
    const messagesEndRef = useRef(null);
    const messageInputRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const conversationSubscriptionRef = useRef(null);

    // Load saved volume and mute settings
    useEffect(() => {
        const savedVolume = localStorage.getItem('messageVolume');
        const savedMuted = localStorage.getItem('messageMuted');

        if (savedVolume) setVolume(parseFloat(savedVolume));
        if (savedMuted) setIsMuted(savedMuted === 'true');
    }, []);

    // Fetch conversations and update badge immediately
    useEffect(() => {
        if (isOpen && userProfile && company) {
            fetchConversations();
            // Refresh badge when opening menu
            if (onRefreshBadge) {
                onRefreshBadge();
            }
        }
    }, [isOpen, userProfile, company, onRefreshBadge]);

    // Auto-scroll to bottom
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    // Focus input when conversation is selected
    useEffect(() => {
        if (selectedConversation && messageInputRef.current) {
            messageInputRef.current.focus();
        }
    }, [selectedConversation]);

    // Subscribe to real-time messages in current conversation
    useEffect(() => {
        if (!selectedConversation?.id) {
            // Clean up subscription if no conversation selected
            if (conversationSubscriptionRef.current) {
                supabase.removeChannel(conversationSubscriptionRef.current);
                conversationSubscriptionRef.current = null;
            }
            return;
        }

        // Clean up previous subscription
        if (conversationSubscriptionRef.current) {
            supabase.removeChannel(conversationSubscriptionRef.current);
        }

        // Subscribe to new messages in this conversation
        const subscription = subscribeToConversationMessages(
            selectedConversation.id,
            (newMessage) => {
                // Only add messages from OTHER users (we already add our own messages optimistically)
                if (newMessage.sender_id === userProfile.id) {
                    return; // Skip our own messages from real-time subscription
                }

                // Only add if not already in messages (avoid duplicates)
                setMessages(prev => {
                    const exists = prev.some(m => m.id === newMessage.id);
                    if (exists) return prev;
                    return [...prev, newMessage];
                });

                // Mark as read if the message is for us
                if (newMessage.receiver_id === userProfile.id) {
                    markMessagesAsRead(selectedConversation.id, userProfile.id);
                    fetchConversations(); // Refresh conversation list
                    if (onRefreshBadge) {
                        onRefreshBadge();
                    }
                }
            }
        );

        conversationSubscriptionRef.current = subscription;

        // Cleanup on unmount or conversation change
        return () => {
            if (conversationSubscriptionRef.current) {
                supabase.removeChannel(conversationSubscriptionRef.current);
                conversationSubscriptionRef.current = null;
            }
        };
    }, [selectedConversation?.id, userProfile?.id, onRefreshBadge]);

    const fetchConversations = async () => {
        try {
            if (!userProfile?.id || !company?.id) return;
            const data = await getConversations(userProfile.id, company.id);
            setConversations(data);
        } catch (error) {
            console.error('Error fetching conversations:', error);
        }
    };

    const fetchMessages = async (conversationId, loadMore = false) => {
        try {
            if (loadMore && loadingMoreMessages) return; // Prevent duplicate requests

            if (loadMore) {
                setLoadingMoreMessages(true);
                // Get the oldest message timestamp for pagination
                const oldestMessage = messages[0];
                const before = oldestMessage ? oldestMessage.created_at : null;

                const data = await getMessages(conversationId, 30, before);

                if (data.length < 30) {
                    setHasMoreMessages(false);
                }

                // Prepend older messages
                setMessages(prev => [...data, ...prev]);
                setLoadingMoreMessages(false);
            } else {
                // Initial load
                const data = await getMessages(conversationId, 30);
                setMessages(data);
                setHasMoreMessages(data.length === 30);
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
            setLoadingMoreMessages(false);
        }
    };

    const fetchAvailableUsers = async () => {
        try {
            if (!company?.id || !userProfile?.id) return;
            setLoadingUsers(true);
            const users = await getAvailableUsers(company.id, userProfile.id);
            setAvailableUsers(users);
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleConversationClick = async (conversation) => {
        setSelectedConversation(conversation);
        setHasMoreMessages(true); // Reset pagination
        await fetchMessages(conversation.id);

        // Mark as read immediately
        if (conversation.unreadCount > 0) {
            await markMessagesAsRead(conversation.id, userProfile.id);
            // Refresh conversations and badge
            await fetchConversations();
            if (onRefreshBadge) {
                onRefreshBadge();
            }
        }
    };

    const handleScroll = (e) => {
        const { scrollTop } = e.target;

        // Load more when scrolled to top
        if (scrollTop === 0 && hasMoreMessages && !loadingMoreMessages && selectedConversation) {
            const previousHeight = messagesContainerRef.current.scrollHeight;
            fetchMessages(selectedConversation.id, true).then(() => {
                // Maintain scroll position after loading older messages
                requestAnimationFrame(() => {
                    const newHeight = messagesContainerRef.current.scrollHeight;
                    messagesContainerRef.current.scrollTop = newHeight - previousHeight;
                });
            });
        }
    };

    const handleCreateConversation = async (userId) => {
        try {
            setLoadingUsers(true);
            const conversation = await getOrCreateConversation(userProfile.id, userId, company.id);
            setSelectedConversation(conversation);
            await fetchMessages(conversation.id);
            setShowNewConversation(false);
            fetchConversations();
        } catch (error) {
            console.error('Error creating conversation:', error);
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleSendMessage = async () => {
        if (!messageText.trim() || !selectedConversation || isLoading) return;

        const content = messageText.trim();

        try {
            setIsLoading(true);
            setMessageText(''); // Clear immediately for better UX

            const newMessage = await sendMessage(
                selectedConversation.id,
                userProfile.id,
                selectedConversation.otherUser.id,
                content
            );

            // Add message immediately to UI
            setMessages(prev => [...prev, newMessage]);

            // Refresh conversations in background
            fetchConversations();
        } catch (error) {
            console.error('Error sending message:', error);
            setMessageText(content); // Restore message on error
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteConversation = async (conversationId, e) => {
        e.stopPropagation();
        if (!window.confirm('Delete this conversation?')) return;

        try {
            await deleteConversation(conversationId);
            if (selectedConversation?.id === conversationId) {
                setSelectedConversation(null);
                setMessages([]);
            }
            fetchConversations();
        } catch (error) {
            console.error('Error deleting conversation:', error);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleVolumeChange = (newVolume) => {
        setVolume(newVolume);
        localStorage.setItem('messageVolume', newVolume.toString());
    };

    const handleMuteToggle = () => {
        const newMuted = !isMuted;
        setIsMuted(newMuted);
        localStorage.setItem('messageMuted', newMuted.toString());
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));

        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes}m`;

        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `${diffInHours}h`;

        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays === 1) return 'Yesterday';
        if (diffInDays < 7) return `${diffInDays}d`;

        return date.toLocaleDateString();
    };

    const formatMessageTime = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const unreadCount = conversations.reduce((sum, conv) => sum + conv.unreadCount, 0);
    const filteredConversations = conversations.filter(conv =>
        conv.otherUser?.username?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getRoleBadgeColor = (role) => {
        switch (role) {
            case 'super_admin':
            case 'admin':
                return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
            case 'designer':
                return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
            case 'estimator':
                return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
            case 'driver':
                return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
            default:
                return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300';
        }
    };

    // Display "Notify Team" for system users
    const getDisplayName = (user) => {
        if (!user) return 'Unknown';
        if (user.role === 'system' || user.username === 'Notify Team') return 'Notify Team';
        return user.full_name || user.username || 'Unknown';
    };

    function capitalizeFirstLetter(string) {
        console.log(string)
        if (!string) return '';
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/20 z-40"
                onClick={onClose}
            />

            {/* Menu - FIXED HEIGHT */}
            <div
                ref={menuRef}
                className={`fixed bottom-4 ${isSidebarOpen ? 'left-[17rem]' : 'left-24'} w-[440px] h-[600px] bg-white dark:bg-zinc-800 rounded-xl shadow-2xl border border-gray-200 dark:border-zinc-700 z-50 overflow-hidden flex flex-col transition-all duration-300`}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-zinc-700 bg-gradient-to-r from-purple-50/80 to-pink-50/80 dark:from-purple-900/20 dark:to-pink-900/20 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        {selectedConversation && (
                            <button
                                onClick={() => {
                                    setSelectedConversation(null);
                                    setMessages([]);
                                }}
                                className="p-1.5 hover:bg-white/70 dark:hover:bg-zinc-700 rounded-lg transition-colors"
                            >
                                <FiArrowLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                            </button>
                        )}
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                            <FiMessageSquare className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white">
                                {selectedConversation ? getDisplayName(selectedConversation.otherUser) : 'Messages'}
                            </h3>
                            {!selectedConversation && unreadCount > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                                    {unreadCount} unread
                                </span>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/70 dark:hover:bg-zinc-700 rounded-xl transition-colors"
                    >
                        <FiX className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </button>
                </div>

                {/* Controls - Only show in conversation list */}
                {!selectedConversation && (
                    <div className="flex flex-col p-3 bg-white/70 dark:bg-zinc-900/50 border-b border-gray-200 dark:border-zinc-700 flex-shrink-0">
                        {/* Volume Controls */}
                        <div className="flex items-center gap-2 mb-3">
                            <button
                                onClick={handleMuteToggle}
                                className={`p-2 rounded-lg transition-all ${isMuted
                                    ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                                    : 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
                                    }`}
                            >
                                {isMuted ? <FiVolumeX className="w-4 h-4" /> : <FiVolume2 className="w-4 h-4" />}
                            </button>

                            {!isMuted && (
                                <div className="flex items-center gap-2 flex-1">
                                    <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.1"
                                        value={volume}
                                        onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                                        className="flex-1 h-2 bg-gray-200 dark:bg-zinc-700 rounded-lg appearance-none cursor-pointer"
                                    />
                                    <button
                                        onClick={() => messageSound.testSound()}
                                        className="px-3 py-1 text-xs font-medium bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50 rounded-lg transition-colors"
                                    >
                                        Test
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Search & New Conversation */}
                        <div className="flex items-center gap-2">
                            <div className="relative flex-1">
                                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                    type="text"
                                    placeholder="Search conversations..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 w-full bg-white dark:bg-zinc-900 dark:text-white"
                                />
                            </div>
                            <button
                                onClick={() => {
                                    setShowNewConversation(true);
                                    fetchAvailableUsers();
                                }}
                                className="px-4 py-2 text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 rounded-lg transition-colors flex items-center gap-2"
                            >
                                <FiMessageSquare className="w-4 h-4" />
                                New
                            </button>
                        </div>
                    </div>
                )}

                {/* Content Area - SCROLLABLE */}
                <div className="flex-1 overflow-hidden">
                    {!selectedConversation ? (
                        // Conversations List - SCROLLABLE
                        <div className="h-full overflow-y-auto">
                            {filteredConversations.length === 0 ? (
                                <div className="p-12 text-center text-gray-500 dark:text-gray-400">
                                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-zinc-700 rounded-2xl flex items-center justify-center">
                                        <FiMessageSquare className="w-6 h-6 text-gray-400" />
                                    </div>
                                    <p className="text-sm font-medium mb-2">No messages yet</p>
                                    <p className="text-xs text-gray-400 leading-relaxed">
                                        Start a conversation with your team members
                                    </p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-100 dark:divide-zinc-700">
                                    {filteredConversations.map((conversation) => (
                                        <div
                                            key={conversation.id}
                                            className={`p-4 hover:bg-gray-50 dark:hover:bg-zinc-700/50 cursor-pointer transition-all group ${conversation.unreadCount > 0 ? 'bg-purple-50/30 dark:bg-purple-900/10' : ''
                                                }`}
                                            onClick={() => handleConversationClick(conversation)}
                                        >
                                            <div className="flex items-start gap-3">
                                                {/* Avatar */}
                                                <div className="relative flex-shrink-0">
                                                    {conversation.otherUser?.avatar_url && conversation.otherUser.role !== 'system' ? (
                                                        <img
                                                            src={conversation.otherUser.avatar_url}
                                                            alt={getDisplayName(conversation.otherUser)}
                                                            className="w-12 h-12 rounded-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold ${conversation.otherUser?.role === 'system' || conversation.otherUser?.username === 'Notify Team'
                                                            ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
                                                            : 'bg-gradient-to-br from-purple-400 to-pink-400'
                                                            }`}>
                                                            {getDisplayName(conversation.otherUser).charAt(0).toUpperCase()}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Content */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between mb-1">
                                                        <h4 className={`text-sm font-semibold ${conversation.unreadCount > 0 ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
                                                            }`}>
                                                            {getDisplayName(conversation.otherUser)}
                                                        </h4>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs text-gray-400">
                                                                {conversation.updated_at ? formatTimestamp(conversation.updated_at) : ''}
                                                            </span>
                                                            <button
                                                                onClick={(e) => handleDeleteConversation(conversation.id, e)}
                                                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-all"
                                                                title="Delete conversation"
                                                            >
                                                                <FiX className="w-3 h-3 text-red-500" />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center justify-between">
                                                        <p className={`text-xs truncate max-w-[240px] ${conversation.unreadCount > 0 ? 'text-gray-700 dark:text-gray-300 font-medium' : 'text-gray-500 dark:text-gray-400'
                                                            }`}>
                                                            {conversation.lastMessage?.content || 'No messages yet'}
                                                        </p>
                                                        {conversation.unreadCount > 0 && (
                                                            <span className="flex-shrink-0 w-5 h-5 bg-purple-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                                                                {conversation.unreadCount > 9 ? '9+' : conversation.unreadCount}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        // Conversation View
                        <div className="h-full flex flex-col">
                            {/* Messages - SCROLLABLE */}
                            <div
                                ref={messagesContainerRef}
                                onScroll={handleScroll}
                                className="flex-1 overflow-y-auto p-4 space-y-4"
                            >
                                {/* Loading more indicator */}
                                {loadingMoreMessages && (
                                    <div className="flex justify-center py-2">
                                        <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                )}

                                {/* Show "No more messages" if at the end */}
                                {!hasMoreMessages && messages.length > 0 && (
                                    <div className="flex justify-center py-2">
                                        <span className="text-xs text-gray-400">No more messages</span>
                                    </div>
                                )}

                                {messages.length > 0 ? (
                                    messages.map((message) => {
                                        const isOwnMessage = message.sender_id === userProfile.id;

                                        return (
                                            <div
                                                key={message.id}
                                                className={`flex gap-2 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                                            >
                                                <div className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'} max-w-[70%]`}>
                                                    <div
                                                        className={`px-4 py-2 rounded-2xl ${isOwnMessage
                                                            ? 'bg-purple-500 text-white rounded-br-md'
                                                            : 'bg-gray-100 dark:bg-zinc-700 text-gray-900 dark:text-white rounded-bl-md'
                                                            }`}
                                                    >
                                                        <p className="text-sm leading-relaxed break-words whitespace-pre-line">
                                                            {message.content}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-1 mt-1 px-1">
                                                        <span className="text-xs text-gray-400">
                                                            {formatMessageTime(message.created_at)}
                                                        </span>
                                                        {isOwnMessage && message.is_read && (
                                                            <FiCheck className="w-3 h-3 text-blue-500" />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="flex items-center justify-center h-full text-gray-400">
                                        <div className="text-center">
                                            <FiMessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                            <p className="text-sm">No messages yet</p>
                                            <p className="text-xs mt-1">Send a message to start the conversation</p>
                                        </div>
                                    </div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Message Input */}
                            <div className="p-4 border-t border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 flex-shrink-0">
                                {selectedConversation?.otherUser?.role === 'system' || selectedConversation?.otherUser?.username === 'Notify Team' ? (
                                    // Read-only state for Notify Team conversations
                                    <div className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                                        <FiMessageSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                        <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                                            This is a system message. Replies are not available.
                                        </p>
                                    </div>
                                ) : (
                                    // Normal message input
                                    <div className="flex items-end gap-2">
                                        <textarea
                                            ref={messageInputRef}
                                            value={messageText}
                                            onChange={(e) => setMessageText(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Type a message..."
                                            rows="1"
                                            disabled={isLoading}
                                            className="flex-1 px-4 py-2 text-sm border border-gray-200 dark:border-zinc-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none max-h-32 disabled:bg-gray-50 dark:bg-zinc-900 dark:text-white disabled:cursor-not-allowed"
                                            style={{ minHeight: '40px' }}
                                        />
                                        <button
                                            onClick={handleSendMessage}
                                            disabled={!messageText.trim() || isLoading}
                                            className={`p-2.5 rounded-xl transition-all ${messageText.trim() && !isLoading
                                                ? 'bg-purple-500 text-white hover:bg-purple-600 shadow-lg'
                                                : 'bg-gray-100 dark:bg-zinc-700 text-gray-400 cursor-not-allowed'
                                                }`}
                                        >
                                            {isLoading ? (
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                                <FiSend className="w-5 h-5" />
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* New Conversation Modal */}
                {showNewConversation && (
                    <div className="absolute inset-0 bg-black/20 backdrop-blur-sm z-10 flex items-center justify-center p-6">
                        <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-2xl w-full h-[500px] flex flex-col">
                            {/* Modal Header */}
                            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-zinc-700 flex-shrink-0">
                                <h3 className="font-semibold text-gray-900 dark:text-white">New Conversation</h3>
                                <button
                                    onClick={() => {
                                        setShowNewConversation(false);
                                        setSearchUsers('');
                                    }}
                                    className="p-1.5 hover:bg-gray-100 dark:hover:bg-zinc-700 rounded-lg transition-colors"
                                >
                                    <FiX className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                </button>
                            </div>

                            {/* Search */}
                            <div className="p-4 border-b border-gray-200 dark:border-zinc-700 flex-shrink-0">
                                <div className="relative">
                                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                        type="text"
                                        placeholder="Search users..."
                                        value={searchUsers}
                                        onChange={(e) => setSearchUsers(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-zinc-900 dark:text-white"
                                    />
                                </div>
                            </div>

                            {/* Users List - SCROLLABLE WITH LOADING */}
                            <div className="flex-1 overflow-y-auto">
                                {loadingUsers ? (
                                    <div className="p-12 text-center">
                                        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Loading users...</p>
                                    </div>
                                ) : availableUsers
                                    .filter(user => user.username.toLowerCase().includes(searchUsers.toLowerCase()))
                                    .length === 0 ? (
                                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                                        <p className="text-sm">No users found</p>
                                    </div>
                                ) : (
                                    availableUsers
                                        .filter(user => user.username.toLowerCase().includes(searchUsers.toLowerCase()))
                                        .map((user) => (
                                            <div
                                                key={user.id}
                                                onClick={() => handleCreateConversation(user.id)}
                                                className="p-4 hover:bg-purple-50/50 dark:hover:bg-purple-900/10 cursor-pointer transition-colors border-b border-gray-100 dark:border-zinc-700 last:border-0"
                                            >
                                                <div className="flex items-center gap-3">
                                                    {user.avatar_url ? (
                                                        <img
                                                            src={user.avatar_url}
                                                            alt={user.username}
                                                            className="w-10 h-10 rounded-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold">
                                                            {user.username.charAt(0).toUpperCase()}
                                                        </div>
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                                                            {capitalizeFirstLetter(userProfile?.full_name?.split(' ').join(" ") || userProfile?.username)}
                                                        </h4>
                                                        <p className={`text-xs px-2 py-0.5 rounded-full inline-block mt-1 ${getRoleBadgeColor(user.role)}`}>
                                                            {user.role.replace('_', ' ').toUpperCase()}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default MessageMenu;
